import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Search } from "lucide-react";

export default function CheckOutFlow() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const search = async (q) => {
    setLoading(true);
    const { data } = await api.get("/visits/search", { params: { q } });
    setResults(data);
    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const checkout = async (id) => {
    const { data } = await api.post(`/visits/${id}/checkout`);
    toast.success(data.badge_returned ? `Visitor checked out — Badge ${data.badge_returned} returned` : "Visitor checked out");
    setResults((r) => r.filter((v) => v.id !== id));
    setTimeout(() => navigate("/kiosk"), 1500);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-brand-navy p-8" data-testid="checkout-flow-page">
      <button onClick={() => navigate("/kiosk")} data-testid="checkout-back-button" className="flex items-center gap-2 text-slate-500 hover:text-brand-navy mb-8 text-sm uppercase tracking-widest">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="max-w-xl mx-auto space-y-6">
        <h1 className="text-3xl font-black uppercase tracking-tighter">Check Out</h1>
        <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              data-testid="checkout-search-input"
              placeholder="Search by name, phone, or badge number"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-14 rounded-sm bg-white border-slate-300 text-lg pl-12"
            />
          </div>
          <div className="space-y-3">
            {!query.trim() && results.length > 0 && (
              <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="checkout-list-heading">
                Currently Checked-In ({results.length})
              </p>
            )}
            {results.map((v) => (
              <div key={v.id} data-testid={`checkout-result-${v.id}`} className="flex items-center justify-between border border-slate-200 bg-white p-4 rounded-md">
                <div>
                  <p className="font-bold text-lg text-brand-navy">{v.visitor_name}</p>
                  <p className="text-sm text-slate-500">
                    Badge {v.badge_number} · Host {v.host_name}
                  </p>
                </div>
                <Button onClick={() => checkout(v.id)} data-testid={`checkout-confirm-${v.id}`} className="h-12 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase gap-2">
                  <LogOut size={18} /> Check Out
                </Button>
              </div>
            ))}
            {!loading && !results.length && (
              <p className="text-slate-400 text-center py-6" data-testid="checkout-empty">
                {query.trim() ? "No checked-in visitor found" : "No visitors currently checked in"}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
